import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  clearReturnTo,
  selectReturnTo,
} from "@/features/payment-methods/store/payment-method-form-slice";
import { useAppDispatch, useAppSelector } from "@/store";
import { AppRoute } from "@/types/routes";

import { resetMileageClaimDraft } from "../store/mileage-claim-draft-slice";
import { resetReceiptClaimDraft } from "../store/receipt-claim-draft-slice";

const FLOW_RESETTERS = {
  [AppRoute.MILEAGE_SUBMIT]: resetMileageClaimDraft,
  [AppRoute.RECEIPT_SUBMIT]: resetReceiptClaimDraft,
} as const;

const PAYMENT_METHOD_CHANGE_PREFIXES = ["/payment-methods", "/card/physical"];

const isPaymentMethodChangeRoute = (pathname: string) =>
  PAYMENT_METHOD_CHANGE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

let previousPathname: string | null = null;

export const useClaimDraftCleanup = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const returnTo = useAppSelector(selectReturnTo);

  useEffect(() => {
    const previous = previousPathname;
    previousPathname = pathname;

    const detourLive =
      returnTo !== null &&
      (pathname === returnTo || isPaymentMethodChangeRoute(pathname));

    // This effect also re-runs when returnTo changes, which lands a render
    // before useLocation reports the new pathname. Reconsidering the detour on
    // that run would drop one naming a route other than the one being left.
    const samePathname = previous !== null && previous === pathname;
    if (!samePathname && returnTo !== null && !detourLive) {
      dispatch(clearReturnTo());
    }

    if (previous === null || previous === pathname) return;
    if (detourLive && returnTo === previous) return;

    const reset = FLOW_RESETTERS[previous as keyof typeof FLOW_RESETTERS];
    if (reset) dispatch(reset());
  }, [pathname, dispatch, returnTo]);
};
